/**
 * Wallet tab: unlock the wallet kept in this browser or open a wallet file; once unlocked, the
 * account, its recent transactions, lock, download and removal. The view shows its locked or unlocked
 * part by the root model's `unlocked`.
 */

import ko from 'knockout';
import type { TransactionRecord } from 'streembit-bc-sdk/api';
import { ModelBase } from './base';
import type { WalletModel } from './wallet';
import { unlockWithPassword, requireSession } from '../session';
import { downloadWalletFile, readWalletFile } from '../platform';
import { saveStoredWallet, deleteStoredWallet } from '../store';
import { toUnits } from '../transactions';

/** One row of the recent transactions table, as the markup shows it. */
export interface TransactionRow {
    time: string;
    direction: string;
    counterparty: string;
    amount: string;
    txid: string;
}

function formatTime(timestamp: number): string {
    if (!timestamp) return '';
    return new Date(timestamp).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function shortTxid(txid: string): string {
    return txid.length > 20 ? `${txid.slice(0, 10)}…${txid.slice(-8)}` : txid;
}

export function transactionRow(tx: TransactionRecord, own: string): TransactionRow {
    const sent = tx.from === own;
    const direction = tx.method ? `${sent ? 'Call' : 'Received'}: ${tx.method}` : sent ? 'Sent' : 'Received';
    const amount = toUnits(tx.amount || '0') === 0n ? '0' : `${sent ? '−' : '+'}${tx.amount}`;
    return { time: formatTime(tx.timestamp), direction, counterparty: sent ? tx.to : tx.from, amount, txid: shortTxid(tx.txid) };
}

export class OpenModel extends ModelBase {
    // unlock form
    readonly password = ko.observable('');
    // open-file form
    readonly chosenFile = ko.observable<File | null>(null);
    readonly filePassword = ko.observable('');
    readonly keepFile = ko.observable(true);

    readonly busy = ko.observable(false);
    readonly confirmingRemoval = ko.observable(false);

    readonly rows = ko.pureComputed(() => this.wallet.transactions().map(tx => transactionRow(tx, this.wallet.address())));
    readonly balanceText = ko.pureComputed(() => {
        const a = this.wallet.account();
        return a ? `${a.balance} SBRIT` : '—';
    });
    readonly sequenceText = ko.pureComputed(() => {
        const a = this.wallet.account();
        return a ? String(a.sequence) : '—';
    });
    readonly heightText = ko.pureComputed(() => {
        const c = this.wallet.chain();
        return c ? String(c.height) : '—';
    });

    constructor(private readonly wallet: WalletModel) {
        super();
    }

    /** `event: { change: fileChosen }` on the file input. */
    fileChosen(_data: unknown, event: Event): void {
        const input = event.target as HTMLInputElement;
        this.chosenFile(input.files && input.files[0] ? input.files[0] : null);
    }

    async unlock(): Promise<void> {
        this.hideErrors();
        const stored = this.wallet.stored();
        if (!stored) return;
        this.busy(true);
        try {
            const session = await unlockWithPassword(stored.file, this.password());
            this.password('');
            this.wallet.startSession(session);
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    async openFile(): Promise<void> {
        this.hideErrors();
        const chosen = this.chosenFile();
        if (!chosen) {
            this.displayErrors('Choose a wallet file first.');
            return;
        }
        this.busy(true);
        try {
            const file = await readWalletFile(chosen);
            const session = await unlockWithPassword(file, this.filePassword());
            this.filePassword('');
            this.chosenFile(null);
            if (this.keepFile()) {
                await saveStoredWallet(file, session.account.address);
                await this.wallet.reloadStored();
            }
            this.wallet.startSession(session);
        } catch (err) {
            this.displayErrors(err);
        } finally {
            this.busy(false);
        }
    }

    refresh(): void {
        this.hideErrors();
        void this.wallet.refresh();
    }

    lock(): void {
        this.hideErrors();
        this.wallet.lock();
    }

    download(): void {
        const s = requireSession();
        downloadWalletFile(s.file, s.account.address);
    }

    askRemoval(): void {
        this.confirmingRemoval(true);
    }

    keep(): void {
        this.confirmingRemoval(false);
    }

    async remove(): Promise<void> {
        try {
            await deleteStoredWallet();
            this.confirmingRemoval(false);
            await this.wallet.reloadStored();
            this.wallet.lock();
            this.wallet.notice('The wallet was removed from this browser.');
        } catch (err) {
            this.displayErrors(err);
        }
    }
}
